import { z } from 'zod';
import { MeetingEventDocumentType } from '../meeting-events/documents/index.js';
// DOCUMENT_NAME is for documents whose own name already identifies a meeting body — prefixing
// those with the meeting's first body would name the wrong one.
export var LaserficheFileNaming;
(function (LaserficheFileNaming) {
    LaserficheFileNaming["MEETING_BODY_PREFIXED"] = "meeting_body_prefixed";
    LaserficheFileNaming["DOCUMENT_NAME"] = "document_name";
})(LaserficheFileNaming || (LaserficheFileNaming = {}));
// The only place a Laserfiche-archivable document type is declared. Drives the support board's
// toggles, settings validation, and the push worker's file naming — adding an entry is all it takes.
export const LASERFICHE_DOCUMENT_TYPE_OPTIONS = [
    {
        value: MeetingEventDocumentType.AGENDA_OUTLINE,
        label: 'Agenda Outline',
        fileNaming: LaserficheFileNaming.MEETING_BODY_PREFIXED,
    },
    {
        value: MeetingEventDocumentType.AGENDA_PACKAGE,
        label: 'Agenda Package',
        fileNaming: LaserficheFileNaming.MEETING_BODY_PREFIXED,
    },
    {
        value: MeetingEventDocumentType.MINUTES,
        label: 'Minutes',
        fileNaming: LaserficheFileNaming.MEETING_BODY_PREFIXED,
    },
    {
        value: MeetingEventDocumentType.MEETING_BODY_MINUTES,
        label: 'Meeting Body Minutes',
        fileNaming: LaserficheFileNaming.DOCUMENT_NAME,
    },
    {
        value: MeetingEventDocumentType.TRANSCRIPT,
        label: 'Transcript',
        fileNaming: LaserficheFileNaming.MEETING_BODY_PREFIXED,
    },
    {
        value: MeetingEventDocumentType.PUBLIC_NOTICE,
        label: 'Public Notice',
        fileNaming: LaserficheFileNaming.MEETING_BODY_PREFIXED,
    },
];
const optionsByDocumentType = new Map(LASERFICHE_DOCUMENT_TYPE_OPTIONS.map((option) => [option.value, option]));
export function isPushableDocumentType(type) {
    return optionsByDocumentType.has(type);
}
// A type dropped from the catalog can still be enabled in a government's stored settings, so this
// falls back to the default shape rather than leaving the document unnameable.
export function getLaserficheFileNaming(type) {
    return optionsByDocumentType.get(type)?.fileNaming ?? LaserficheFileNaming.MEETING_BODY_PREFIXED;
}
export function normalizeEnabledDocumentTypes(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        return {};
    }
    return Object.fromEntries(Object.entries(value).filter(([, v]) => typeof v === 'boolean'));
}
export function isDocumentTypeEnabled(enabledDocumentTypes, type) {
    return normalizeEnabledDocumentTypes(enabledDocumentTypes)[type] === true;
}
export const laserficheSettingsUpsertSchema = z.object({
    repositoryId: z
        .string('Repository ID is required')
        .min(1, 'Repository ID is required')
        .max(255, 'Repository ID must be less than 255 characters'),
    govinityFolderEntryId: z
        .number('Folder entry ID is required')
        .int('Folder entry ID must be a whole number')
        .positive('Folder entry ID must be a positive number'),
    enabledDocumentTypes: z
        .record(z.string(), z.boolean())
        .refine((map) => Object.keys(map).every(isPushableDocumentType), {
        message: 'Unsupported document type',
    }),
    isActive: z.boolean(),
    servicePrincipalKey: z
        .string()
        .optional()
        .describe('Omit or send an empty string to keep the stored key.'),
    accessKey: z.string().optional().describe('Omit or send an empty string to keep the stored key.'),
});
/**
 * Partial edits to an existing configuration. Omitted fields keep their stored values.
 */
export const laserficheSettingsUpdateSchema = laserficheSettingsUpsertSchema.partial();
/**
 * Initial configuration requires both credentials. Updates may omit them to keep
 * the stored values, so they stay optional on {@link laserficheSettingsUpsertSchema}.
 */
export const laserficheSettingsCreateSchema = laserficheSettingsUpsertSchema.extend({
    servicePrincipalKey: z
        .string('Service Principal Key is required')
        .min(1, 'Service Principal Key is required'),
    accessKey: z.string('Access Key is required').min(1, 'Access Key is required'),
});
